import { mskCherezChasRovno, mskDlyaPolya, mskIzPolya, mskSlovami } from '../../../lib/vremyaMsk';
import { useState } from 'react';
import { useText } from '../../clients/words';

/**
 * Окна ходов лида (`s635`): «Передать», «Отказ…», «СМС клиентке». Общие для
 * карточки лида доски и меню ходов «Обращений» — одно окно на одно действие.
 */

/** «Передать»: кому — списком людей, причина — списком из словаря (op-rules §31.9). */
export function Peredat({
  lyudi,
  prichiny,
  onOtmena,
  onGotovo,
}: {
  lyudi: Array<{ id: number; name: string }>;
  prichiny: Array<{ code: string; label: string }>;
  onOtmena: () => void;
  onGotovo: (userId: number, prichina: string) => void;
}) {
  const t = useText();
  const [kto, setKto] = useState<number | null>(null);
  const [prichina, setPrichina] = useState<string | null>(null);
  const [slova, setSlova] = useState('');
  const itog = prichina !== null
    ? [prichiny.find((p) => p.code === prichina)?.label ?? prichina, slova.trim()].filter((s) => s !== '').join(' — ')
    : slova.trim();

  return (
    <div className="lid-shtorka-fon" onClick={onOtmena}>
      <div className="lid-shtorka" onClick={(e) => e.stopPropagation()}>
        <h4>{t('doska.karta.komu')}</h4>
        <div className="spisok">
          {lyudi.map((l) => (
            <button key={l.id} type="button" className={kto === l.id ? 'aktiv' : ''} onClick={() => setKto(l.id)}>
              {l.name}
            </button>
          ))}
        </div>
        <h4>{t('doska.karta.prichina')}</h4>
        <div className="spisok">
          {prichiny.map((p) => (
            <button key={p.code} type="button" className={prichina === p.code ? 'aktiv' : ''} onClick={() => setPrichina(p.code)}>
              {p.label}
            </button>
          ))}
        </div>
        <textarea rows={2} value={slova} onChange={(e) => setSlova(e.target.value)} placeholder={t('doska.karta.slova')} />
        <div className="knopki">
          <button type="button" className="doska-knopka" onClick={onOtmena}>{t('doska.karta.otmena')}</button>
          <button
            type="button"
            className="doska-knopka glavnaya"
            disabled={kto === null || itog === ''}
            onClick={() => kto !== null && onGotovo(kto, itog)}
          >
            {t('doska.karta.peredat')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** «Отказ…»: причина — списком из справочника, слова — по желанию (op-rules §31.9). */
export function Otkaz({
  prichiny,
  opisanie,
  onOtmena,
  onGotovo,
}: {
  prichiny: Array<{ id: number; label: string }>;
  /** `s722`: что именно закрывается и что будет с записью — словами сервера. */
  opisanie?: { obyekt: string; posledstviya: string; zapis: boolean } | null;
  onOtmena: () => void;
  onGotovo: (reasonId: number, slova: string | null) => void;
}) {
  const t = useText();
  const [vybrano, setVybrano] = useState<number | null>(null);
  const [slova, setSlova] = useState('');

  return (
    <div className="lid-shtorka-fon" onClick={onOtmena}>
      <div className="lid-shtorka" onClick={(e) => e.stopPropagation()}>
        {opisanie != null && (
          <div className={opisanie.zapis ? 'lid-okno-poyasnenie vazhno' : 'lid-okno-poyasnenie'} data-testid="otkaz-opisanie">
            <b>{opisanie.obyekt}</b>
            <span>{opisanie.posledstviya}</span>
          </div>
        )}
        <h4>{t('doska.karta.prichina')}</h4>
        <div className="spisok">
          {prichiny.map((p) => (
            <button key={p.id} type="button" className={vybrano === p.id ? 'aktiv' : ''} onClick={() => setVybrano(p.id)}>
              {p.label}
            </button>
          ))}
        </div>
        <textarea rows={2} value={slova} onChange={(e) => setSlova(e.target.value)} placeholder={t('doska.karta.slova')} />
        <div className="knopki">
          <button type="button" className="doska-knopka" onClick={onOtmena}>{t('doska.karta.otmena')}</button>
          <button
            type="button"
            className="doska-knopka glavnaya"
            disabled={vybrano === null}
            onClick={() => vybrano !== null && onGotovo(vybrano, slova.trim() === '' ? null : slova.trim())}
          >
            {t('doska.karta.otkaz')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** «СМС клиентке» — своими словами; уходит дверью `s632` с запись-ключом. */
export function SmsOkno({ onOtmena, onGotovo }: { onOtmena: () => void; onGotovo: (tekst: string) => void }) {
  const t = useText();
  const [tekst, setTekst] = useState('');

  return (
    <div className="lid-shtorka-fon" onClick={onOtmena}>
      <div className="lid-shtorka" onClick={(e) => e.stopPropagation()}>
        <h4>{t('doska.karta.sms')}</h4>
        <textarea rows={4} value={tekst} onChange={(e) => setTekst(e.target.value)} />
        <div className="knopki">
          <button type="button" className="doska-knopka" onClick={onOtmena}>{t('doska.karta.otmena')}</button>
          <button type="button" className="doska-knopka glavnaya" disabled={tekst.trim() === ''} onClick={() => onGotovo(tekst.trim())}>
            {t('doska.karta.otpravit')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * «ЗАПЛАНИРОВАТЬ ЗВОНОК» (`s722`, решение владельца 06.10.2026: «Перезвон: понятны клиент, дата,
 * время, часовой пояс и результат; комментарий необязательный, действие „Запланировать звонок“»).
 *
 * Прежнее окно было одним полем даты с кнопкой «Готово»: кому звоним, по каким часам время и что
 * будет с лидом — не говорилось. Сотрудника окно не спрашивает: персональных ответственных нет,
 * автором остаётся тот, кто запланировал.
 *
 * Время вводится по часам компьютера (поле браузера) — пояс назван словами; на сервер уходит
 * момент целиком, поэтому на другом компьютере он покажется в его поясе верно.
 */
export function ZaplanirovatZvonok({
  komu,
  perepiska = false,
  onOtmena,
  onGotovo,
}: {
  /** Клиентка: имя или номер под маской; `null` — неизвестна. */
  komu: string | null;
  /** Не лид, а переписка: результат называется иначе. */
  perepiska?: boolean;
  onOtmena: () => void;
  onGotovo: (iso: string, kommentariy: string | null) => void;
}) {
  const izSlovarya = useText();
  const t = (kod: string, zapas: string, vars?: Record<string, string>) => {
    const slovo = izSlovarya(kod, vars);
    if (slovo !== kod) return slovo;

    return Object.entries(vars ?? {}).reduce((x, [n, v]) => x.split(`{${n}}`).join(v), zapas);
  };
  // `s731`: поле — московские часы на любом компьютере (см. `lib/vremyaMsk`).
  const [kogda, setKogda] = useState(() => mskDlyaPolya(mskCherezChasRovno(new Date())));
  const [kommentariy, setKommentariy] = useState('');
  // «Сейчас» — состоянием (чтение часов при отрисовке запрещено): обновляется с каждой правкой времени,
  // а при нажатии кнопки время сверяется с часами заново.
  const [seychas, setSeychas] = useState(() => Date.now());
  const moment = mskIzPolya(kogda);
  const vBudushchem = moment !== null && moment.getTime() > seychas;
  const poyas = t('doska.karta.zvonok.msk', 'по Москве');
  const kogdaSlovami = moment === null ? '—' : mskSlovami(moment);
  const kto = komu ?? '—';

  return (
    <div className="lid-shtorka-fon" onClick={onOtmena}>
      <div className="lid-shtorka" onClick={(e) => e.stopPropagation()} data-testid="okno-zvonok">
        <h4>{t('doska.karta.zvonok.zagolovok', 'Запланировать звонок')}</h4>
        <div className="lid-okno-pole">
          <span>{t('doska.karta.zvonok.komu', 'Кому звоним')}</span>
          <b data-testid="zvonok-komu">{kto}</b>
        </div>
        <label className="lid-okno-pole">
          <span>{t('doska.karta.zvonok.kogda', 'Дата и время')}</span>
          <input type="datetime-local" value={kogda} onChange={(e) => { setKogda(e.target.value); setSeychas(Date.now()); }} data-testid="zvonok-kogda" />
        </label>
        <div className="lid-okno-podpis" data-testid="zvonok-poyas">{t('doska.karta.zvonok.poyas_msk', 'Время московское — на любом компьютере.')}</div>
        <textarea rows={2} value={kommentariy} onChange={(e) => setKommentariy(e.target.value)} placeholder={t('doska.karta.zvonok.kommentariy', 'Комментарий — по желанию')} data-testid="zvonok-kommentariy" />
        <div className={vBudushchem ? 'lid-okno-poyasnenie' : 'lid-okno-poyasnenie vazhno'} data-testid="zvonok-rezultat">
          <span>
            {vBudushchem
              ? t(perepiska ? 'doska.karta.zvonok.rezultat_perepiska' : 'doska.karta.zvonok.rezultat',
                perepiska ? 'Звонок {кому}: {когда}. Переписка вернётся в очередь к этому времени.' : 'Звонок {кому}: {когда}. Лид перейдёт в «Перезвонить позже».',
                { кому: kto, когда: `${kogdaSlovami}, ${poyas}` })
              : t('doska.karta.zvonok.proshloe', 'Выберите время в будущем.')}
          </span>
        </div>
        {/* Что напомнит о звонке: отметка вида «пора перезвонить» на плитке и строка в колокольчике шапки. */}
        <div className="lid-okno-podpis" data-testid="zvonok-napominanie">
          {t('doska.karta.zvonok.napominanie', 'В назначенное время лид получит отметку «пора перезвонить» на доске и появится в колокольчике в шапке.')}
        </div>
        <div className="knopki">
          <button type="button" className="doska-knopka" onClick={onOtmena}>{izSlovarya('doska.karta.otmena')}</button>
          <button
            type="button"
            className="doska-knopka glavnaya"
            disabled={!vBudushchem}
            data-testid="zvonok-zaplanirovat"
            onClick={() => {
              if (moment === null) return;
              // окно могло простоять открытым: прошедшее время не уходит на сервер
              if (moment.getTime() <= Date.now()) return setSeychas(Date.now());
              onGotovo(moment.toISOString(), kommentariy.trim() === '' ? null : kommentariy.trim());
            }}
          >
            {t('doska.karta.zvonok.zaplanirovat', 'Запланировать звонок')}
          </button>
        </div>
      </div>
    </div>
  );
}
